import './Calendar.scss'
import {
  DAYS_OF_WEEK,
  getFirstDayOfWeek,
  getLastDayOfWeek,
  getNextWeekDay,
  getPreviousWeekDay,
  getWeekInformation,
  MONTHS_OF_YEAR,
  type day,
  type MealType,
} from '../../../utils/calendarUtils'
import { Fragment, useEffect, useMemo, useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import Meal from './Meal'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import Spinner from '../../Spinner/Spinner'
import {
  AlertTime,
  getRecipeList,
  type recipe,
} from '../../../utils/recipeUtils'
import { getMessage } from '../../../utils/messageUtils'
import type { Mode } from '../../../App'

interface CalendarProps {
  user: User
  isMobile: boolean
  setMode: (mode: Mode) => void
}

function Calendar({ user, isMobile, setMode }: CalendarProps) {
  const [day, setDay] = useState(new Date())
  const [dayMobile, setDayMobile] = useState(new Date())
  const [weekInfo, setWeekInfo] = useState<day[] | undefined>(undefined)

  const [recipeList, setRecipeList] = useState<recipe[] | undefined>(undefined)
  const [loadRecipeList, setLoadRecipeList] = useState(true)

  const [popupOpen, setPopupOpen] = useState(false)
  const [popupMessage, setPopupMessage] = useState('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupOnOk, setPopupOnOk] = useState<() => void>(() => {})
  const [popupExtraButtons, setPopupExtraButtons] = useState<
    ExtraButton[] | undefined
  >(undefined)

  const [emptyWeekPopupOpen, setEmptyWeekPopupOpen] = useState(false)
  const [createWeek, setCreateWeek] = useState(false)

  const [isLoading, setIsLoading] = useState(true)

  const firstDayOfWeek = useMemo(() => getFirstDayOfWeek(day), [day])
  const lastDayOfWeek = useMemo(() => getLastDayOfWeek(day), [day])
  const month = firstDayOfWeek.getMonth()

  useEffect(() => {
    const fetchWeekInfo = async () => {
      try {
        const data = await getWeekInformation(
          firstDayOfWeek,
          lastDayOfWeek,
          createWeek,
          user.id!
        )
        setCreateWeek(false)
        if (data.empty) {
          setEmptyWeekPopupOpen(true)
          setWeekInfo(undefined)
        } else {
          setWeekInfo(data.days)
        }
      } finally {
        setIsLoading(false)
      }
    }
    fetchWeekInfo()
  }, [firstDayOfWeek, lastDayOfWeek, createWeek, user.id])

  useEffect(() => {
    const fetchRecipeList = async () => {
      const data = await getRecipeList(user.id!)
      if (data.success) {
        setRecipeList(data.recipes)
        setLoadRecipeList(false)
      }
    }

    if (loadRecipeList || !recipeList) {
      fetchRecipeList()
    }
  }, [recipeList, loadRecipeList, user.id])

  const updateWeekInfo = (
    day: number,
    mealType: MealType,
    recipeId: number | null,
    fromTupper: boolean,
    alert: string | null,
    alertTime: AlertTime | null
  ) => {
    if (!weekInfo) return
    const newWeekInfo = [...weekInfo]
    const dayInfo = { ...newWeekInfo[day] }
    dayInfo[mealType] = recipeId
    dayInfo[`${mealType}_from_tupper`] = fromTupper
    dayInfo[`${mealType}_alert`] = alert
    dayInfo[`${mealType}_alertTime`] = alertTime
    newWeekInfo[day] = dayInfo
    setWeekInfo(newWeekInfo)
    setLoadRecipeList(true)
  }

  const openPopup = (
    message: string,
    children: React.ReactNode,
    extraButtons: ExtraButton[]
  ) => {
    setPopupMessage(message)
    setPopupChildren(children)
    setPopupExtraButtons(extraButtons)
    setPopupOpen(true)
  }

  const closePopup = () => {
    setPopupOpen(false)
  }

  const previousDayClickMobile = () => {
    const previousDay = new Date(dayMobile)
    previousDay.setDate(previousDay.getDate() - 1)
    setDayMobile(previousDay)

    // Sunday. We need to load the previous week
    if (previousDay.getDay() === 0) {
      setIsLoading(true)
      setDay(previousDay)
    }
  }

  const nextDayClickMobile = () => {
    const nextDay = new Date(dayMobile)
    nextDay.setDate(nextDay.getDate() + 1)
    setDayMobile(nextDay)

    // Monday. We need to load the next week
    if (nextDay.getDay() === 1) {
      setIsLoading(true)
      setDay(nextDay)
    }
  }

  const nextWeeckClick = () => {
    setIsLoading(true)
    const nextWeekDay = getNextWeekDay(lastDayOfWeek)
    setDay(nextWeekDay)
  }

  const previousWeeckClick = () => {
    setIsLoading(true)
    const previousWeekDay = getPreviousWeekDay(firstDayOfWeek)
    setDay(previousWeekDay)
  }

  const getHeaderDays = (): React.ReactElement[] => {
    const headerDays: React.ReactElement[] = []
    const currentDay = new Date(firstDayOfWeek)
    for (let i = 0; i < 7; i++) {
      headerDays.push(
        <div
          key={`calendar-header-day-${i}`}
          className="calendar-header-day calendar-box"
        >
          {DAYS_OF_WEEK[i]} {currentDay.getDate()}
        </div>
      )
      currentDay.setDate(currentDay.getDate() + 1)
    }
    return headerDays
  }

  const generateMeal = (
    recipeId: number | null,
    type: MealType,
    date: string,
    comesFromTupper: boolean,
    alert: string | null,
    alertTime: AlertTime | null,
    i: number
  ) => {
    return (
      <Meal
        recipeId={recipeId}
        type={type}
        date={date}
        dateDay={DAYS_OF_WEEK[i]}
        dateId={i}
        comesFromTupper={comesFromTupper}
        alert={alert}
        alertTime={alertTime}
        user={user}
        existingRecipes={recipeList}
        updateWeekInfo={updateWeekInfo}
        openPopup={openPopup}
        closePopup={closePopup}
        setPopupOnOk={setPopupOnOk}
        setMode={setMode}
        isMobile={isMobile}
      />
    )
  }

  const getWeekDay = (date: Date): number => {
    const weekDay = date.getDay() - 1
    return weekDay === -1 ? 6 : weekDay
  }

  const generateCalendarDayMobile = () => {
    if (!weekInfo) {
      return null
    }
    const weekDay = getWeekDay(dayMobile)
    const day = weekInfo[weekDay]
    const date = day.date || ''
    return (
      <div className="calendar-body">
        <div className="calendar-desayuno-row calendar-row">
          {generateMeal(
            day.desayuno,
            'desayuno',
            date,
            day.desayuno_from_tupper,
            day.desayuno_alert,
            day.desayuno_alertTime,
            weekDay
          )}
        </div>
        <div className="calendar-almuerzo-row calendar-row">
          {generateMeal(
            day.almuerzo,
            'almuerzo',
            date,
            day.almuerzo_from_tupper,
            day.almuerzo_alert,
            day.almuerzo_alertTime,
            weekDay
          )}
        </div>
        <div className="calendar-comida-row calendar-row">
          {generateMeal(
            day.comida,
            'comida',
            date,
            day.comida_from_tupper,
            day.comida_alert,
            day.comida_alertTime,
            weekDay
          )}
        </div>
        <div className="calendar-merienda-row calendar-row">
          {generateMeal(
            day.merienda,
            'merienda',
            date,
            day.merienda_from_tupper,
            day.merienda_alert,
            day.merienda_alertTime,
            weekDay
          )}
        </div>
        <div className="calendar-cena-row calendar-row">
          {generateMeal(
            day.cena,
            'cena',
            date,
            day.cena_from_tupper,
            day.cena_alert,
            day.cena_alertTime,
            weekDay
          )}
        </div>
      </div>
    )
  }

  const generateCalendar = () => {
    if (!weekInfo) {
      return null
    }

    const desayuno = []
    const almuerzo = []
    const comida = []
    const merienda = []
    const cena = []
    for (let i = 0; i < 7; i++) {
      const day = weekInfo[i]
      const date = day.date || ''
      desayuno.push(
        generateMeal(
          day.desayuno,
          'desayuno',
          date,
          day.desayuno_from_tupper,
          day.desayuno_alert,
          day.desayuno_alertTime,
          i
        )
      )
      almuerzo.push(
        generateMeal(
          day.almuerzo,
          'almuerzo',
          date,
          day.almuerzo_from_tupper,
          day.almuerzo_alert,
          day.almuerzo_alertTime,
          i
        )
      )
      comida.push(
        generateMeal(
          day.comida,
          'comida',
          date,
          day.comida_from_tupper,
          day.comida_alert,
          day.comida_alertTime,
          i
        )
      )
      merienda.push(
        generateMeal(
          day.merienda,
          'merienda',
          date,
          day.merienda_from_tupper,
          day.merienda_alert,
          day.merienda_alertTime,
          i
        )
      )
      cena.push(
        generateMeal(
          day.cena,
          'cena',
          date,
          day.cena_from_tupper,
          day.cena_alert,
          day.cena_alertTime,
          i
        )
      )
    }

    return (
      <div className="calendar-body">
        <div className="calendar-desayuno-row calendar-row">{desayuno}</div>
        <div className="calendar-almuerzo-row calendar-row">{almuerzo}</div>
        <div className="calendar-comida-row calendar-row">{comida}</div>
        <div className="calendar-merienda-row calendar-row">{merienda}</div>
        <div className="calendar-cena-row calendar-row">{cena}</div>
      </div>
    )
  }

  const generatePopup = () => {
    return (
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        extraButtons={popupExtraButtons}
        isMobile={isMobile}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
    )
  }

  const generateDateSelector = () => {
    if (isMobile) {
      return (
        <div className="calendar-selector-bar">
          <div
            className="calendar-selector-arrow"
            onClick={previousDayClickMobile}
          >
            {'<'}
          </div>
          <div className="calendar-selector-content">
            {dayMobile.getDate()} {' - '} {MONTHS_OF_YEAR[dayMobile.getMonth()]}
            {' - '} {DAYS_OF_WEEK[getWeekDay(dayMobile)]}
          </div>
          <div className="calendar-selector-arrow" onClick={nextDayClickMobile}>
            {'>'}
          </div>
        </div>
      )
    } else {
      return (
        <div className="calendar-selector-bar">
          <div className="calendar-selector-arrow" onClick={previousWeeckClick}>
            {'<'}
          </div>
          <div className="calendar-selector-content">
            {firstDayOfWeek.getDate()} {' - '}
            {lastDayOfWeek.getDate()} {' - '} {MONTHS_OF_YEAR[month]}
          </div>
          <div className="calendar-selector-arrow" onClick={nextWeeckClick}>
            {'>'}
          </div>
        </div>
      )
    }
  }

  if (!weekInfo) {
    return (
      <div className="calendar-container">
        {generateDateSelector()}
        <div className="calendar-empty-state">
          <p className="calendar-empty-state-message">
            {getMessage('no_week_info')}
          </p>
        </div>
        <Popup
          isOpen={emptyWeekPopupOpen}
          message={getMessage('create_week_confirm')}
          isMobile={isMobile}
          onOk={() => {
            setIsLoading(true)
            setCreateWeek(true)
            setEmptyWeekPopupOpen(false)
          }}
          onClose={() => setEmptyWeekPopupOpen(false)}
        />
        <Spinner
          isOpen={isLoading}
          overlay={true}
          message={getMessage('loading')}
        />
      </div>
    )
  }

  if (isMobile) {
    return (
      <Fragment>
        <div className="calendar-container">
          {generateDateSelector()}
          <div className="calendar-body-container">
            {generateCalendarDayMobile()}
          </div>
        </div>
        {generatePopup()}
        <Spinner isOpen={isLoading} overlay={true} message="Loading..." />
      </Fragment>
    )
  }

  return (
    <Fragment>
      <div className="calendar-container">
        {generateDateSelector()}
        <div className="calendar-body-container">
          <div className="calendar-header calendar-row">{getHeaderDays()}</div>
          {generateCalendar()}
        </div>
      </div>
      {generatePopup()}
      <Spinner
        isOpen={isLoading}
        overlay={true}
        message={getMessage('loading')}
      />
    </Fragment>
  )
}

export default Calendar
